import { useEffect, useState } from "react";
import { Share, Plus, X } from "lucide-react";
import { detectOS } from "@/lib/device";

const DISMISS_KEY = "cleanplate.iosInstallPrompt.dismissedAt";
const COOLDOWN_DAYS = 7;

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  // iOS Safari home-screen apps expose `navigator.standalone`.
  if ((window.navigator as any).standalone === true) return true;
  // PWAs in general expose the standalone display mode.
  return window.matchMedia?.("(display-mode: standalone)").matches ?? false;
};

const isIOSSafari = () => {
  if (typeof navigator === "undefined") return false;
  if (detectOS() !== "ios") return false;
  const ua = navigator.userAgent;
  // Exclude in-app browsers (Instagram, FB, Chrome on iOS, etc.) — Add to
  // Home Screen only works from real Safari.
  if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|FBAN|FBAV|Instagram|Line\/|TikTok/i.test(ua)) {
    return false;
  }
  return /Safari/i.test(ua);
};

/**
 * Bottom-sheet hint that nudges iOS Safari users to "Add to Home Screen"
 * so the app runs in standalone mode. This unlocks the Screen Wake Lock
 * API and reliable WebAudio playback for cooking timers — both of which
 * are heavily restricted in regular Safari tabs.
 *
 * Only renders on iPhone Safari, never inside a PWA, and respects a
 * 7-day cooldown after the user dismisses it.
 */
export const IosInstallPrompt = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIOSSafari() || isStandalone()) return;
    try {
      const last = localStorage.getItem(DISMISS_KEY);
      if (last) {
        const ms = Date.now() - Number(last);
        if (ms < COOLDOWN_DAYS * 24 * 60 * 60 * 1000) return;
      }
    } catch {
      // localStorage may be blocked in private mode — show anyway.
    }
    // Tiny delay so the prompt doesn't flash before the page is interactive.
    const t = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 z-50 flex justify-center px-3"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
      role="dialog"
      aria-label="Install CleanPlate to your home screen"
    >
      <div className="w-full max-w-sm rounded-2xl border border-border bg-background/98 p-4 shadow-lg backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">
              Install CleanPlate for the best experience
            </p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Tap{" "}
              <span className="inline-flex items-center gap-1 align-middle">
                <Share className="inline h-3.5 w-3.5" />
                <span className="font-medium">Share</span>
              </span>{" "}
              then{" "}
              <span className="inline-flex items-center gap-1 align-middle">
                <Plus className="inline h-3.5 w-3.5" />
                <span className="font-medium">Add to Home Screen</span>
              </span>
              . Cooking timers and the keep-screen-on feature only work fully
              in the installed app.
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="-mr-1 -mt-1 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
